import {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Checkbox from "../../../../components/Checkbox";
import { styles } from "../../../../styles/themes/style";
import { capitalLetters, getNameInitials, formatNumber } from "../../../../utils";
import { useAppStore } from "../../../../stores/application/useAppStore";
import ActionText from "../../../../components/ActionText";
import styled from "styled-components";
import Text from "../../../../components/Text";
import ProfilePhoto from "../../../../components/ProfilePhoto";
import useMediaQuery from "../../../../components/useMediaQuery";
import SelectAllCheckbox from "../../../../components/SelectAllCheckbox";
import Row from "../../../../components/Row";
import LoaderCell from "../../../../components/LoaderCell";
import { default as ColumnComponent } from "../../../../components/Column";
import DataGrid, { Column } from "react-data-grid";
import { IProfile } from "../../../../interfaces/models/profile";
import { useConnectionsStore } from "../../../../stores/connections/useConnectionsStore";
import { useConnectionsHandlers } from "../../../../services/actions/connections/useConnectionsHandlers";
import { ITag } from "../../../../interfaces/models/connection";
import AppTag from "../../../../fragments/AppTag";
import moment from "moment";
import EmptyPage from "../../../../components/EmptyPage";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import { useTagsHandlers } from "../../../../services/actions/tags/useTagsHandlers";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import SvgFragment from "../../../../fragments/SvgFragment";

interface IProspectsTableProps {
  profilesData: any[];
  profilesCount: number;
  isAllSelectedProps?: {
    isAllSelected: boolean;
    setIsAllSelected: (e: any) => void;
  };
  selectedProfiles: any[];
  selectedProfilesChange: (profiles: any[]) => void;
  addToLeadDeltaHandler: (row: any) => void;
  isBulkAdding: boolean;
}

const ProspectsTable: FunctionComponent<IProspectsTableProps> = ({
  profilesData,
  profilesCount,
  isAllSelectedProps,
  selectedProfiles,
  selectedProfilesChange,
  addToLeadDeltaHandler,
  isBulkAdding,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const isSmall = useMediaQuery("(max-width: 768px)");
  const {
    store: { requestLoading },
  } = useAppStore();
  const [profilesLoading, setProfilesLoading] = useState<boolean>(false);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState(20);
  const addingToLeadDeltaProfile = useRef<string | null>(null);
  const addingToLeadDelta = useRef<boolean>(false);
  const [workspaceUsers, setWorkspaceUsers] = useState<any[]>([]);
  const { workspaceUsersPublicAllHandler } = useTagsHandlers();
  const { store: { user, workspace } } = useAuthStore();
  const { setAddMembersModal } = useAppHandlers();
  const [memberSearch, setMemberSearch] = useState<string>('');
  const [membersPanelRowId, setMembersPanelRowId] = useState<string | null>(null);
  const membersPanelCloseTimeoutRef = useRef<number | null>(null);
  const isHoveringMembersPanelRef = useRef<boolean>(false);

  const cancelMembersPanelClose = () => {
    if (membersPanelCloseTimeoutRef.current) {
      window.clearTimeout(membersPanelCloseTimeoutRef.current);
      membersPanelCloseTimeoutRef.current = null;
    }
  };

  const scheduleMembersPanelClose = (delayMs = 120) => {
    cancelMembersPanelClose();
    membersPanelCloseTimeoutRef.current = window.setTimeout(() => {
      if (isHoveringMembersPanelRef.current) return;
      setMembersPanelRowId(null);
    }, delayMs);
  };

  useEffect(() => {
    setProfiles(profilesData);
    setVisibleCount(profilesData?.length);
    addingToLeadDelta.current = false;
    addingToLeadDeltaProfile.current = null;
  }, [profilesData]);

  //const { pageSize, total } = tagsParameters;
  const { isAllSelected, setIsAllSelected } = isAllSelectedProps || {};

  useEffect(() => {
    if (isBulkAdding) {
      addingToLeadDelta.current = true;
    }
  }, [isBulkAdding]);

  const noResultTitle = "Discover Top Prospects";
  const noResultDescription = "Search for profiles by city, job title, company name, industry, etc.";

  const { store: { connectionsParameters } } = useConnectionsStore()
  const { prospectsColumnsSetup } = connectionsParameters;

  const localStorageColumnsSetup = window.localStorage.getItem('prospects-columns-setup');
  const visibilitySettings = localStorageColumnsSetup
    ? JSON.parse(localStorageColumnsSetup)
    : prospectsColumnsSetup;
 
  type ColumnWidths = {
    [key: string]: number;
    };

    interface RenderCellProps {
        row: IProfile;
        column?: any
    }
  
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({});

  const { setConnectionsParametersHandler } = useConnectionsHandlers()
   
  const [resizableKeys, setResizableKeys] = useState<string[]>([]);

  useEffect(() => {
    if ("getProspects" in requestLoading && !addingToLeadDelta.current)
      setProfilesLoading(requestLoading?.["getProspects"]);
  }, [requestLoading]);

  useEffect(() => {
    let isMounted = true;
    const loadWorkspaceUsers = async () => {
      try {
        const result = await workspaceUsersPublicAllHandler({
          condition: "or",
          name: "",
          email: "",
        });
        if (isMounted) setWorkspaceUsers(result || []);
      } catch (e) {
        // ignore - UI fallback handled by empty array
      }
    };
    loadWorkspaceUsers();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (membersPanelCloseTimeoutRef.current) {
        window.clearTimeout(membersPanelCloseTimeoutRef.current);
      }
    };
  }, []);

  const selectAllCheckboxHandler = () => {
    selectedProfilesChange(profiles);
    setIsAllSelected && setIsAllSelected(!isAllSelected);
  };

  const selectCheckboxHandler = (e: React.MouseEvent<HTMLElement>, id: any) => {
    e.stopPropagation();
    const index = selectedProfiles.indexOf(id);
    if (index !== -1) {
      const updatedIds = selectedProfiles.filter(
        (profileId: any) => profileId !== id
      );
      selectedProfilesChange(updatedIds);
    } else {
      const updatedIds = [...selectedProfiles, id];
      selectedProfilesChange(updatedIds);
    }
  };

  const selectOnThisPageHadler = () => {
    selectedProfilesChange(profiles);
    setIsAllSelected && setIsAllSelected(!isAllSelected);
  };

  const clearHandler = () => {
    selectedProfilesChange([]);
    setIsAllSelected && setIsAllSelected(false);
  };

  const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({
    text,
    children,
  }) => {
    return (
      <TooltipComponent>
        {children}
        <div>
          <span>{text}</span>
        </div>
      </TooltipComponent>
    );
  };


  const Preloader: React.FC<{}> = () => (
    <ColumnComponent gap="3px">
      {Array(50)
        .fill(undefined)
        .map((x: any, index: number) => (
          <PreloaderRow key={index} gap="18px">
            {Array(5)
              .fill(undefined)
              .map((x: any, indexCol: number) => (
                <LoaderCell
                  key={indexCol}
                  $width={`${100 / 5}%`}
                  $height="37px"
                />
              ))}
          </PreloaderRow>
        ))}
    </ColumnComponent>
  );

  const handleHeaderMouseEnter = (key: string) => {
    setResizableKeys(prev => prev.includes(key) ? prev : [...prev, key]);
  };

  let minColumnWidth = 200
  // Define columns array
  const initialColumns: Column<IProfile>[] = useMemo(() => [
      {
          key: 'userInfo',
          name: '',
          width: isSmall ? 240 : 300,
          minWidth: minColumnWidth,
          frozen: !isSmall,
          resizable: resizableKeys.includes('userInfo'),
          headerCellClass: 'user-info-cell-class',
          renderHeaderCell: () => (
              <HeaderCell onMouseEnter={() => handleHeaderMouseEnter('userInfo')}>
                  <>
                      <div style={{position: 'absolute'}}>
                          <SelectAllCheckbox
                              isCheckboxChecked={selectedProfiles.length > 0 || isAllSelected || false}
                              pageSize={visibleCount}
                              total={visibleCount}
                              paginated={visibleCount}
                              isAllSelected={isAllSelected || false}
                              selected={selectedProfiles.length}
                              label={'profiles'}
                              selectAllHandler={selectAllCheckboxHandler}
                              selectOnThisPageHadler={selectOnThisPageHadler}
                              clearHandler={clearHandler}
                              backgroundColor='white'
                          />
                      </div>
                      <HeaderPhotoNameWrapper gap='8px'>
                          {!isSmall && <Text $lighter $heading6>Name</Text>}
                          {!isSmall && <div style={{width: '20px'}} />}
                      </HeaderPhotoNameWrapper>
                      <Resize onMouseEnter={() => handleHeaderMouseEnter('userInfo')} />
                  </>
              </HeaderCell>
          ),
          cellClass: (row) => {
             // const info = infoHandler(row);
              return `'user-info'}`;
          },
          renderCell: ({ row, column }: RenderCellProps) => {
              //const info = infoHandler(row)
              const columnWidth = columnWidths[column.key] || column.width;

              return (
                  <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                      <div style={{paddingRight: '8px'}}><Checkbox
                          checked={selectedProfiles.some((selectedProfile: IProfile) => selectedProfile.linkedin_id === row.linkedin_id || isAllSelected)}
                          checkboxId={row.linkedin_id}
                          onClickHandler={(e: React.MouseEvent<HTMLElement>) => selectCheckboxHandler(e, row)}
                      /></div>
                      <CellPhotoNameWrapper alignItems gap='5px'>
                          {!isSmall && <ProfilePhoto tooltipMessage={row?.name} capitalLetters={getNameInitials(row)} source={row.image_url} />}
                          <div style={{ width: `${columnWidth - (isSmall ? 110 : 170)}px` }}>
                              <Tooltip text={`${row.name}`}>
                                  <Text $ellipsis='0' $bold>{row.name}</Text>
                              </Tooltip>
                          </div>
                      </CellPhotoNameWrapper>
                  </div>
              )
          }
      },
      {
        key: 'addToLeadDelta',
        name: <Text $lighter $heading6>Add to Lead Delta</Text>,
        resizable: false,
        minWidth: 165,
        maxWidth: 165,
        cellClass: (row) => {
            return `truncate-cell`;
        },
        renderHeaderCell: () => (
            <HeaderCell>
                <Text $lighter $heading6>Add to Lead Delta</Text>
            </HeaderCell>
        ),
        renderCell: ({ row }) => (
          (addingToLeadDelta.current && row?.linkedin_id === addingToLeadDeltaProfile.current)
            ? <div key={"loading-" + row.linkedin_id}><LoadingSpinner /></div>
            : <AddToLDCell key={"add-" + row.linkedin_id + "-" + addingToLeadDelta.current + "-" + addingToLeadDeltaProfile.current} row={row} disabled={addingToLeadDelta.current || isBulkAdding} />
        )
      },
      {
        key: 'mutualConnections',
        minWidth: 150,
        maxWidth: 150,
        name: <Text $lighter $heading6>Connected members</Text>,
        resizable: resizableKeys.includes('mutualConnections'),
        cellClass: (row) => {
            //const info = infoHandler(row);
            return '';
        },
        renderCell: ({ row }: RenderCellProps) => {
          if (workspace?.isStarter) return null;
          const isSoloWorkspace = workspaceUsers?.length === 1 && workspaceUsers?.[0]?._id === user?._id;
          if (isSoloWorkspace) {
            return (
              <ConnectedMembersCell>
                <AddMemberHoverButton
                  type="button"
                  className="add-member-button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setAddMembersModal();
                  }}
                >
                  <SvgFragment type="plusBlue" />
                  <span>Add members</span>
                </AddMemberHoverButton>
              </ConnectedMembersCell>
            );
          }
          const users = Array.isArray(row?.users) ? row.users : [];
          const colWidth = (columnWidths['mutualConnections'] ?? 150) as number;
          const availableWidth = Math.max(0, colWidth - 16);
          const step = 22;
          const maxVisible = Math.max(1, Math.floor((availableWidth + 2) / step));
          const visibleUsers = users.slice(0, maxVisible);
          const hiddenCount = Math.max(0, users.length - visibleUsers.length);
          return (
            <StyledRowShared
              onMouseEnter={() => {
                cancelMembersPanelClose();
                setMembersPanelRowId(row?.linkedin_id || null);
              }}
              onMouseLeave={() => {
                scheduleMembersPanelClose();
              }}
            >
              {visibleUsers.map((u: any) => (
                <OverlappingPhoto
                  key={u?._id}
                  className={`connection-version-${u?.version}`}
                  source={u?.profilePicture}
                  allowTooltip={false}
                  capitalLetters={`${(u?.firstName || "").charAt(0).toUpperCase()}${(u?.lastName || "").charAt(0).toUpperCase()}`}
                />
              ))}
              {hiddenCount > 0 && <MoreMembersPill>{`+${hiddenCount} more`}</MoreMembersPill>}
              {membersPanelRowId === row?.linkedin_id && (
                <InfoBox
                  isLastColumn={(storeColumnsOrder ?? columns.map(c => c.key)).at(-1) === 'mutualConnections'}
                  onMouseEnter={() => {
                    isHoveringMembersPanelRef.current = true;
                    cancelMembersPanelClose();
                  }}
                  onMouseLeave={() => {
                    isHoveringMembersPanelRef.current = false;
                    scheduleMembersPanelClose(0);
                  }}
                >
                  <MembersPanelHeader>
                    <MembersSearchInput
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search member"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <MembersCount>{`Members (${(row?.users || []).length})`}</MembersCount>
                  </MembersPanelHeader>
                  <MembersList>
                    {(row?.users || [])
                      .filter((u: any) => {
                        const normalize = (s: string) =>
                          (s || "")
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .toLowerCase();

                        const q = normalize(memberSearch.trim());
                        if (!q) return true;
                        const full = normalize(
                          `${u?.firstName || ""} ${u?.lastName || ""}`.trim()
                        );
                        return full.includes(q);
                      })
                      .map((u: any) => (
                        <UserInfo
                          key={u._id}
                          user={u}
                          autotag={row?.autotagsArray?.find((tag: ITag) => tag?._id === u._id)?.autoTags[0]}
                        />
                      ))}
                  </MembersList>
                </InfoBox>
              )}
            </StyledRowShared>
          );
        }
      },
      {
          key: 'headline',
          name: <Text $lighter $heading6>Headline</Text>,
          resizable: resizableKeys.includes('headline'),
          minWidth: minColumnWidth,
          renderHeaderCell: () => (
              <HeaderCell>
                  <Text $lighter $heading6>Headline</Text>
                  <Resize onMouseEnter={() => handleHeaderMouseEnter('headline')} />
              </HeaderCell>
          ),
          cellClass: (row) => {
              //const info = infoHandler(row);
              return `truncate-cell`;
          },
          renderCell: ({ row, column }: RenderCellProps) => {
              const columnWidth = column.width;
              return (
                  <div style={{ width: `${columnWidth - 10}px` }}>
                      <Text $ellipsis='0'>{row.title}</Text>
                  </div>
              )
          }
      },
      {
          key: 'jobTitle',
          name: <Text $lighter $heading6>Job title</Text>,
          resizable: resizableKeys.includes('jobTitle'),
          minWidth: minColumnWidth,
          renderHeaderCell: () => (
              <HeaderCell>
                  <Text $lighter $heading6>Job title</Text>
                  <Resize onMouseEnter={() => handleHeaderMouseEnter('jobTitle')} />
              </HeaderCell>
          ),
          cellClass: (row) => {
              //const info = infoHandler(row);
              return `truncate-cell`;
          },
          renderCell: ({ row, column }: RenderCellProps) => {
              const columnWidth = column.width;
              return (
                  <div style={{ width: `${columnWidth - 10}px` }}>
                      <Text $ellipsis='0'>{row.position}</Text>
                  </div>
              )
          }
      },
      {
          key: 'company',
          name: <Text $lighter $heading6>Company</Text>,
          resizable: resizableKeys.includes('company'),
          minWidth: minColumnWidth,
          cellClass: (row) => {
              //const info = infoHandler(row);
              return `truncate-cell`;
          },
          headerCellClass: 'company-header-cell-class',
          renderHeaderCell: () => (
              <HeaderCell>
                  <Text $lighter $heading6>Company</Text>
                  <Resize onMouseEnter={() => handleHeaderMouseEnter('company')} />
              </HeaderCell>
          ),
          renderCell: ({ row, column }: RenderCellProps) => {
              const columnWidth = column.width;
              return (
                  <div style={{ width: `${columnWidth - 10}px` }}>
                      <Text $ellipsis='0'>{row.company?.name}</Text>
                  </div>
              )
          }
      },
      {
          key: 'location',
          name: <Text $lighter $heading6>Location</Text>,
          resizable: resizableKeys.includes('location'),
          minWidth: minColumnWidth,
          cellClass: (row) => {
              //const info = infoHandler(row);
              return `truncate-cell`;
          },
          renderHeaderCell: () => (
              <HeaderCell>
                  <Text $lighter $heading6>Location</Text>
                  <Resize onMouseEnter={() => handleHeaderMouseEnter('location')} />
              </HeaderCell>
          ),
          renderCell: ({ row, column }: RenderCellProps) => {
              const columnWidth = column.width;
              return (
                  <div style={{ width: `${columnWidth - 10}px` }}>
                      <Text $ellipsis='0'>{row.location?.city}</Text>
                  </div>
              )
          }
      },
      {
          key: 'languages',
          name: <Text $lighter $heading6>Languages</Text>,
          resizable: resizableKeys.includes('languages'),
          minWidth: minColumnWidth,
          cellClass: (row) => {
              //const info = infoHandler(row);
              return `truncate-cell`;
          },
          renderHeaderCell: () => (
              <HeaderCell>
                  <Text $lighter $heading6>Languages</Text>
                  <Resize onMouseEnter={() => handleHeaderMouseEnter('languages')} />
              </HeaderCell>
          ),
          renderCell: ({ row }: RenderCellProps) => (
              <Row>
                  <Text>
                    {Array.isArray(row?.languages)
                      ? row.languages.join(', ')
                      : typeof row?.languages === 'object' && row?.languages !== null
                        ? Object.values(row.languages).join(', ')
                        : ''}
                  </Text>
              </Row>
          )
      },
      {
          key: 'followers',
          name: <Text $lighter $heading6>Followers</Text>,
          resizable: resizableKeys.includes('followers'),
          minWidth: 100,
          maxWidth: 100,
          cellClass: (row) => {
              //const info = infoHandler(row);
              return `truncate-cell`;
          },
          renderHeaderCell: () => (
              <HeaderCell>
                  <Text $lighter $heading6>Followers</Text>
                  {/* <Resize onMouseEnter={() => handleHeaderMouseEnter('followers')} /> */}
              </HeaderCell>
          ),
          renderCell: ({ row }: RenderCellProps) => (
              <Text>{row?.social_followers ? formatNumber(row?.social_followers) : ''}</Text>
          )
      },
      {
        key: 'linkedIn',
        name: <Text $lighter $heading6>LinkedIn</Text>,
        resizable: false,
        minWidth: 100,
        maxWidth: 100,
        cellClass: (row) => {
            //const info = infoHandler(row);
            return `truncate-cell`;
        },
        renderHeaderCell: () => (
            <HeaderCell>
                <Text $lighter $heading6>LinkedIn</Text>
            </HeaderCell>
        ),
        renderCell: ({ row }: RenderCellProps) => (
            <ActionText
                icon="linkedin"
                onClickHandler={() => window.open(row?.linkedin_url, "_blank")}
            />
        )
      }
  ], [isSmall, columnWidths, selectedProfiles.length, isAllSelected, resizableKeys, workspace?.isStarter, workspaceUsers, user?._id, setAddMembersModal, memberSearch, membersPanelRowId]);


  // Use prospectsColumnsOrder from store
  const storeColumnsOrder = connectionsParameters.prospectsColumnsOrder;

  const orderedColumns = useMemo(() => {
    const order = storeColumnsOrder ?? initialColumns.map((col) => col.key);
    const keyToCol = Object.fromEntries(
      initialColumns.map((col) => [col.key, col])
    );
    const ordered = order
      .map((key: string) => keyToCol[key])
      .filter(Boolean);
    const missing = initialColumns.filter(
      (col) => !order.includes(col.key)
    );
    return [...ordered, ...missing];
  }, [storeColumnsOrder, initialColumns]);

  const columns = orderedColumns;

  // Get all visible columns (including userInfo)
  const visibleColumns = columns.filter(
    (col: Column<IProfile>) => visibilitySettings[col.key]
  );
  const rawTableWidth = tableRef?.current?.offsetWidth ?? 0;

  // Calculate widths: use saved width if available, else default (min or initial)
  const colWidths = visibleColumns.map((col: Column<IProfile>) => {
    if (typeof columnWidths[col.key] === "number") return columnWidths[col.key];
    if (typeof col.width === "number") return col.width;
    return Math.max(minColumnWidth, rawTableWidth / visibleColumns.length);
  });

  // Stretch last column if needed
  const totalColsWidth = colWidths.reduce(
    (sum: number, w: number) => sum + w,
    0
  );
  let adjustedColWidths = [...colWidths];
  if (totalColsWidth < rawTableWidth && adjustedColWidths.length > 0) {
    adjustedColWidths[adjustedColWidths.length - 1] +=
      rawTableWidth - totalColsWidth;
  }

  // Map widths back to columns
  const columnsWithWidths = columns.map((col: Column<IProfile>) => {
    const visibleIdx = visibleColumns.findIndex(
      (c: Column<IProfile>) => c.key === col.key
    );
    return visibleIdx !== -1
      ? { ...col, width: adjustedColWidths[visibleIdx] }
      : col;
  });

  const handleColumnResize = (idx: number, width: number) => {
    setColumnWidths(prev => {
        const updated = { ...prev, [columns[idx].key]: width }
        localStorage.setItem('connection-table-widths', JSON.stringify(updated))
        return updated
    });
  };

  const handleColumnsReorder = (sourceColumnKey: string, targetColumnKey: string) => {
    const sourceIdx = columns.findIndex(col => col.key === sourceColumnKey);
    const targetIdx = columns.findIndex(col => col.key === targetColumnKey);
    if (sourceIdx === -1 || targetIdx === -1) return;
    const newColumns = [...columns];
    const [removed] = newColumns.splice(sourceIdx, 1);
    newColumns.splice(targetIdx, 0, removed);

    // Remove userInfo from newColumns, and add it to the beginning
    const withoutUserInfo = newColumns.filter(col => col.key !== 'userInfo');
    const userInfoCol = columns.find(col => col.key === 'userInfo');
    const finalColumns = userInfoCol ? [userInfoCol, ...withoutUserInfo] : withoutUserInfo;

    const newOrder = finalColumns.map(col => col.key);
    localStorage.setItem('prospects-columns-order', JSON.stringify(newOrder));
    setConnectionsParametersHandler({ ...connectionsParameters, prospectsColumnsOrder: newOrder });
  };

  const [autotags] = useState<ITag[]>(
    [
        {
          _id: "New Connection",
          title: "New Connection",
          color: "#5964ff",
          isForbidden: true,
        },
        {
          _id: "LinkedIn 1st",
          title: "LinkedIn 1st",
          color: "#5964ff",
          isForbidden: true,
        },
        {
          _id: "Disconnected",
          title: "Disconnected",
          color: "#8C96AD",
          isForbidden: true,
        },
        {
          _id: "LinkedIn",
          title: "LinkedIn",
          color: "#5964ff",
          isForbidden: true,
        },
        {
          _id: "Prospect",
          title: "Prospect",
          color: "#5964ff",
          isForbidden: true,
        },
        {
          _id: "CSV",
          title: "CSV",
          color: "#5964ff",
          isForbidden: true,
        },
        {
          _id: "Imported",
          title: "Imported",
          color: "#5964ff",
          isForbidden: true,
        },
        {
          _id: "Removal Scheduled",
          title: "Removal Scheduled",
          color: "#8c96ad",
          isForbidden: true,
        },
      ]
  );

  const UserInfo: React.FC<{ user: any; autotag: any; lastMessaged?: any; removedFromLD?: any; }> = ({ user, autotag, lastMessaged, removedFromLD }) => {
    let tag = autotags.find((tag) => tag.title === autotag?.title);
    if(tag){
        tag.removedFromLD = removedFromLD;
    }
    return (
        <InfoBoxColumn gap='12px'>
            <Row gap='8px'>
                <ProfilePhotoColumn>
                <ProfilePhoto 
                source={user?.profilePicture} 
                capitalLetters={`${(user?.firstName || '').charAt(0).toUpperCase()}${(user?.lastName || '').charAt(0).toUpperCase()}`}  />
                </ProfilePhotoColumn>

                <ColumnComponent>
                    <Text>{user?.firstName + ' ' + user?.lastName}</Text>
                    <TextDescription>{`Last msg: ${lastMessaged?.lastMessagedAt ? moment(lastMessaged?.lastMessagedAt).format('MMM D, YYYY [at] HH:mm') : `n/a`}`}</TextDescription>
                    {tag && <AppTag tag={tag}/> }
                </ColumnComponent>
            </Row>
        </InfoBoxColumn>
    );
  };

  const AddToLDCell: React.FC<{ row: any, disabled: boolean }> = ({row, disabled}) => {
    const addingDisabled = row?.users?.length > 0;
    return addingDisabled ? (
      <Text></Text>
    ) : (
      <AddToLDCellContainer>
        <AddToLDCellText
          icon="profileAdd"
          disabled={disabled}
          onClickHandler={(e: React.MouseEvent<HTMLElement>) =>
            addToLDCellClickHandler(e, row)
          }
        >
          Add to LeadDelta
        </AddToLDCellText>
      </AddToLDCellContainer>
    );
  };

  const addToLDCellClickHandler = (e: React.MouseEvent<HTMLElement>, row: any) => {
    addingToLeadDeltaProfile.current = row?.linkedin_id;
    const mappedProfile = {
      profile: {
        followerCount: row.followerCount ?? undefined,
        profileId: row.profileId ?? row.linkedin_id ?? undefined,
        entityUrn: row.entityUrn ?? undefined,
        firstName: row.firstName ?? (row.name ? row.name.split(' ')[0] : undefined),
        lastName: row.lastName ?? (row.name ? row.name.split(' ').slice(1).join(' ') : undefined),
        headline: row.headline ?? row.title ?? undefined,
        profilePicture: row.image_url ?? row.profilePicture ?? undefined,
        location: row.location
          ? (row.location.address
              ? `${row.location.address}`
              : row.location.city || undefined)
          : undefined,
        country: row.location?.state ?? undefined,
        industry: row.industry ?? undefined,
        company: row.company?.name ?? row.company ?? undefined,
        jobTitle: row.jobTitle ?? row.position ?? undefined,
        languages: Array.isArray(row?.languages)
            ? row.languages?.flat()
            : typeof row?.languages === 'object' && row?.languages !== null
              ? Object.values(row.languages).flat()
              : [],
        publicIdentifier: row.publicIdentifier ?? row.linkedin_id ?? undefined,
      },
      isProspect: true
    };
    addingToLeadDelta.current = true;
    addToLeadDeltaHandler(mappedProfile);
    console.log('addToLDCellClickHandler');
  };

  return (
    <div
      ref={tableRef}
      style={{
        width: "100%",
        height: isSmall ? "50vh" : "calc(100vh - 250px)",
        overflow: "hidden",
        minHeight: "400px",
        WebkitOverflowScrolling: "touch",
        marginTop: "16px",
        backgroundColor: styles.colors.uiBackground,
      }}
    >
      {profilesLoading ? (
        <Preloader />
      ) : !!profiles.length ? (
        <>
          <StyledDataGrid
            columns={columnsWithWidths.filter(
              (item: any) => visibilitySettings[item?.key]
            )}
            rows={profiles.slice(0, visibleCount)}
            rowKeyGetter={(row) => row.linkedin_id}
            // onCellClick={(args) =>
            //   openSidebar(SidebarTypes.CONNECTION_ACTIONS, {
            //     connection: args.row,
            //   })
            // }
            onColumnResize={handleColumnResize}
            className="rdg-light"
            direction="ltr"
            rowHeight={40}
            defaultColumnOptions={{
              draggable: false,
            }}
            onColumnsReorder={handleColumnsReorder}
          />
        </>
      ) : (
        <EmptyPage
          svgType='searchEmpty'
          title={noResultTitle}
          label={noResultDescription}
          buttonTitle='Watch demo video'
          onClick={() => window.open('https://www.loom.com/share/28e7d345ea2a49609d857c6cae0d1e6a?sid=e748dfd7-71fe-4d7d-8906-fd6391fa65ee', '_blank')}
        />
      )}
    </div>
  );
};
const CellPhotoNameWrapper = styled(Row)`
  width: 200px;
`;
const TooltipComponent = styled.div`
  position: relative;

  &:hover div {
    display: block;
  }

  & div {
    display: none;
    position: absolute;
    top: -30px;
    z-index: 2;

    & span {
      border-radius: 3px;
      padding: 4px;
      font-size: 12px;
      background-color: ${({ theme: { septenaryColor } }) => septenaryColor};
      color: ${({ theme: { secondaryColor } }) => secondaryColor};
      white-space: nowrap;
    }
  }
`;
const PreloaderRow = styled(Row)`
  background: ${({ theme: { secondaryColor } }) => secondaryColor};
  align-items: center;
  padding: 10px 5px;
`;
const StyledDataGrid = styled(DataGrid<IProfile>)<{ os?: string }>`
  height: 100%;
  border: 2px solid ${styles.colors.black200};
  background-color: ${styles.colors.uiBackground};

  [role="row"] {
    background-color: ${styles.colors.white} !important;
  }

  [role="row"]:not(:first-of-type):hover {
    background-color: ${styles.colors.black100} !important;
    cursor: pointer;
  }

  .rdg-cell {
    border-right: 1px solid ${styles.colors.black200};
    border-bottom: 1px solid ${styles.colors.black200};
    padding: 0 8px 0 8px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    overflow: visible;
    width: auto;
  }

  .rdg-header-row {
    .rdg-cell {
      cursor: default;

      &:not(:first-child) {
        cursor: grab;

        &:active {
          cursor: grabbing;
        }
      }
    }
  }

  .rdg-header-row {
    position: sticky;
    top: 0;
    z-index: 3;
  }

  .action-cell-class {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  }

  .user-info-cell-class {
    overflow: visible;
  }

  .company-header-cell-class {
    cursor: default;
  }

  .truncate-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .disabled-state {
    background-color: #e7edf3;
  }

  .user-info {
    z-index: 2;
  }
`;
const HeaderPhotoNameWrapper = styled(Row)`
    padding-left: 28px;
`
const HeaderCell = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
`
const Resize = styled.div`
    width: 4px;
    height: 20px;
    background: #DAE0E8; 
    position: absolute;
    right: 5px;
    border-radius: 2px;
`
const StyledRow = styled(Row)`
    position: relative;
    & > *:not(:first-child) {
        margin-left: -2px;
    }
`
const StyledRowShared = styled(StyledRow)`
    cursor: pointer;

    & > *:nth-child(1) {
        margin-left: 0 !important;
        z-index: 1;
    }
    
    & > *:nth-child(2) {
        z-index: 2;
    }
    
    & > *:nth-child(3) {
        z-index: 3;
    }

    & > *:nth-child(4) {
        z-index: 4;
    }
`

const ConnectedMembersCell = styled(StyledRowShared)`
    position: relative;

    .add-member-button {
        opacity: 0;
        pointer-events: none;
    }

    &:hover .add-member-button {
        opacity: 1;
        pointer-events: auto;
    }
`

const AddMemberHoverButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 8px;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    transition: opacity 0.15s ease, background-color 0.15s ease;

    span {
        font-family: NunitoSans;
        font-size: 14px;
        font-weight: 400;
        font-style: normal;
        line-height: 20px;
        letter-spacing: 0px;
        color: ${styles.colors.black600};
        white-space: nowrap;
    }

    &:hover {
        background: ${styles.colors.black100};
    }

    svg {
        width: 12px;
        height: 12px;
    }
`
const InfoBox = styled.div<{ isLastColumn?: boolean }>`
    position: absolute;
    top: 100%;
    left: ${({isLastColumn}) => isLastColumn ? '-220px' : '0'};
    width: 328px;
    height: 330px;
    background-color: ${styles.colors.white};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 16px;
    z-index: 100;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
`

const MembersPanelHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
`

const MembersSearchInput = styled.input`
    width: 100%;
    height: 40px;
    padding: 10px 12px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 6px;
    outline: none;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    background: ${styles.colors.white};

    &::placeholder {
        color: ${styles.colors.black300};
    }

    &:focus {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.12);
    }
`

const MembersCount = styled.div`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0px;
    color: ${styles.colors.black600};
`

const MembersList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 6px;
`
const OverlappingPhoto = styled(ProfilePhoto)`
  margin-bottom: 4px;
  width: 24px;
  height: 24px;
`;

const MoreMembersPill = styled.div`
    margin-left: 6px;
    height: 24px;
    padding: 0 8px;
    border-radius: 12px;
    background: transparent;
    display: flex;
    align-items: center;
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0px;
    color: ${styles.colors.black600};
    white-space: nowrap;
`
const InfoBoxColumn = styled(ColumnComponent)`
    margin-bottom: 12px;
`
const ProfilePhotoColumn = styled(ColumnComponent)`
    max-height: 32px;
`
const TextDescription = styled(Text)`
    color: ${styles.colors.black300};
    margin-bottom: 4px;
    font-size: 12px;
    line-height: 18px;
`
const AddToLDCellContainer = styled.div`
    display: flex;
    width: fit-content;
    align-items: center;
    padding: 0px 6px 0px 4px;
    border-radius: 4px;
    border: 1px solid transparent;
`
const AddToLDCellText = styled(ActionText)<{ disabled: boolean }>`
    span {    
        font-size: 14px;
    }

    div {
        margin-right: 4px;
    }

    ${({ disabled }) => disabled && `
    cursor: default;
    pointer-events: none;
    span, &:hover span, &:focus span{
        cursor: default;
        color: ${styles.colors.disabledState};
    }
`}
`

export default ProspectsTable;
